import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { customerApi, apiError, deviceId } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Brand } from "@/components/Brand";
import { LanguageSelector } from "@/components/LanguageSelector";
import { Turnstile } from "@/components/Turnstile";
import { ArrowLeft, Loader2, Phone, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const nav = useNavigate();
  const { loginSuccess } = useAuth();
  const { t, te, setLang } = useLanguage();
  const [step, setStep] = useState("login"); // login | migrate | forgot | forgot-verify
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");
  const [code, setCode] = useState("");
  const [newPin, setNewPin] = useState("");
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [codeSent, setCodeSent] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const finish = (data) => {
    loginSuccess(data.token, data.customer);
    if (data.customer.language) setLang(data.customer.language);
    toast.success(t("welcome_name", { name: data.customer.name.split(" ")[0] }));
    nav("/home");
  };

  const submit = async () => {
    if (phone.trim().length < 8) return toast.error(t("err_enter_mobile"));
    if (!/^\d{4}$/.test(pin)) return toast.error(t("err_enter_pin"));
    setBusy(true);
    try {
      const { data } = await customerApi.post("/auth/login", { phone: phone.trim(), pin });
      if (data.needs_verification) {
        setStep("migrate");
        setCodeSent(false);
        toast.info(t("migrate_desc"));
      } else {
        finish(data);
      }
    } catch (e) {
      toast.error(te(apiError(e)));
    } finally {
      setBusy(false);
    }
  };

  const sendCode = async (purpose) => {
    if (!token) return toast.error(t("complete_security_check"));
    if (purpose === "reset" && phone.trim().length < 8) return toast.error(t("err_enter_mobile"));
    setBusy(true);
    try {
      await customerApi.post("/auth/send-code", {
        phone: phone.trim(), purpose, turnstile_token: token, device_id: deviceId(),
      });
      setCodeSent(true);
      setCooldown(60);
      if (purpose === "reset") setStep("forgot-verify");
      toast.success(t("toast_code_sent"));
    } catch (e) {
      toast.error(te(apiError(e)));
    } finally {
      setBusy(false);
    }
  };

  const verifyMigration = async () => {
    if (!/^\d{6}$/.test(code)) return toast.error(t("err_code6"));
    setBusy(true);
    try {
      const { data } = await customerApi.post("/auth/verify-migration", { phone: phone.trim(), pin, code });
      finish(data);
    } catch (e) {
      toast.error(te(apiError(e)));
    } finally {
      setBusy(false);
    }
  };

  const resetPin = async () => {
    if (!/^\d{6}$/.test(code)) return toast.error(t("err_code6"));
    if (!/^\d{4}$/.test(newPin)) return toast.error(t("err_pin4"));
    setBusy(true);
    try {
      const { data } = await customerApi.post("/auth/reset-pin", { phone: phone.trim(), code, new_pin: newPin });
      finish(data);
    } catch (e) {
      toast.error(te(apiError(e)));
    } finally {
      setBusy(false);
    }
  };

  const goBack = () => {
    if (step === "login") return nav("/");
    setStep("login"); setCode(""); setNewPin(""); setToken(""); setCodeSent(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <LanguageSelector />
      <div className="max-w-md mx-auto px-6 py-8">
        <button data-testid="login-back-btn" onClick={goBack} className="flex items-center gap-2 text-primary font-medium mb-6">
          <ArrowLeft className="h-5 w-5" /> {t("back")}
        </button>
        <Brand subtitle={step === "login" ? t("welcome_back") : t("verify_your_mobile")} />

        {step === "login" && (
          <div className="mt-8 space-y-5 animate-slide-up">
            <div>
              <label className="block text-sm font-semibold text-primary mb-2">{t("mobile_number_short")}</label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-primary/60" />
                <input data-testid="login-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)}
                  inputMode="tel" placeholder={t("mobile_ph")}
                  className="w-full h-14 pl-12 pr-4 rounded-2xl border border-input bg-white text-base focus:ring-2 focus:ring-primary focus:outline-none" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-primary mb-2">{t("pin_label")}</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-primary/60" />
                <input data-testid="login-pin-input" value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  inputMode="numeric" type="password" placeholder="••••"
                  className="w-full h-14 pl-12 pr-4 rounded-2xl border border-input bg-white text-base tracking-[0.5em] focus:ring-2 focus:ring-primary focus:outline-none" />
              </div>
            </div>
            <button data-testid="login-submit-btn" onClick={submit} disabled={busy} className="btn-primary">
              {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : t("login")}
            </button>
            <button data-testid="login-forgot-btn" onClick={() => { setStep("forgot"); setToken(""); }}
              className="w-full text-center text-sm text-muted-foreground hover:text-primary transition-colors">
              {t("forgot_pin")}
            </button>
            <button data-testid="login-to-register-btn" onClick={() => nav("/register")}
              className="w-full text-center text-sm text-muted-foreground hover:text-primary transition-colors">
              {t("new_customer_register")}
            </button>
          </div>
        )}

        {step === "migrate" && (
          <div className="mt-8 space-y-5 animate-slide-up" data-testid="migrate-panel">
            <div className="flex items-start gap-3 rounded-2xl bg-secondary p-4">
              <ShieldCheck className="h-6 w-6 text-primary shrink-0" />
              <p className="text-sm text-secondary-foreground">{t("migrate_title")}. {t("migrate_desc")}</p>
            </div>
            {!codeSent ? (
              <>
                <Turnstile onToken={setToken} />
                <button data-testid="migrate-sendcode-btn" onClick={() => sendCode("migrate")} disabled={busy || !token} className="btn-primary">
                  {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : t("send_code")}
                </button>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">{t("code_sent_to", { phone })}</p>
                <input data-testid="migrate-code-input" value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric" placeholder={t("code_ph")} className="input-lg tracking-[0.4em] text-center" />
                <button data-testid="migrate-verify-btn" onClick={verifyMigration} disabled={busy} className="btn-primary">
                  {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : t("verify_continue")}
                </button>
                <button data-testid="migrate-resend-btn" onClick={() => sendCode("migrate")} disabled={busy || cooldown > 0}
                  className="w-full text-center text-sm text-muted-foreground hover:text-primary disabled:opacity-60 transition-colors">
                  {cooldown > 0 ? t("resend_in", { s: cooldown }) : t("resend_code")}
                </button>
              </>
            )}
          </div>
        )}

        {step === "forgot" && (
          <div className="mt-8 space-y-5 animate-slide-up" data-testid="forgot-panel">
            <p className="text-sm text-muted-foreground">{t("forgot_desc")}</p>
            <div>
              <label className="block text-sm font-semibold text-primary mb-2">{t("mobile_number_short")}</label>
              <input data-testid="forgot-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)}
                inputMode="tel" placeholder={t("mobile_ph")} className="input-lg" />
            </div>
            <Turnstile onToken={setToken} />
            <button data-testid="forgot-sendcode-btn" onClick={() => sendCode("reset")} disabled={busy || !token} className="btn-primary">
              {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : t("send_code")}
            </button>
          </div>
        )}

        {step === "forgot-verify" && (
          <div className="mt-8 space-y-5 animate-slide-up" data-testid="forgot-verify-panel">
            <p className="text-sm text-muted-foreground">{t("code_sent_to", { phone })}</p>
            <input data-testid="forgot-code-input" value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric" placeholder={t("code_ph")} className="input-lg tracking-[0.4em] text-center" />
            <div>
              <label className="block text-sm font-semibold text-primary mb-2">{t("new_pin_label")}</label>
              <input data-testid="forgot-newpin-input" value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                inputMode="numeric" type="password" placeholder="••••" className="input-lg tracking-[0.5em] text-center" />
            </div>
            <button data-testid="forgot-reset-btn" onClick={resetPin} disabled={busy} className="btn-primary">
              {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : t("verify_reset")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
